// src/components/SearchTool.js
import React, { useState, useCallback } from 'react';
import { searchByEpa, getStateStatus } from '../utils/picolApi';
import { findEpaByTradeName } from '../utils/epaIndex';
import { getHHPSummary, checkIngredientStatus, getCriteriaLabels } from '../data/hhpData';
import ResultCard from './ResultCard';

const SEARCH_TYPES = [
  { id: 'tradeName', label: 'Trade Name', placeholder: 'e.g. Roundup, Manzate, Pristine' },
  { id: 'epaNumber', label: 'EPA Reg. Number', placeholder: 'e.g. 524-445' },
  { id: 'ingredient', label: 'Active Ingredient', placeholder: 'e.g. Chlorpyrifos, Mancozeb' },
];

export default function SearchTool() {
  const [searchType, setSearchType] = useState('tradeName');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSearch = useCallback(async () => {
    if (!query.trim()) return;
    setLoading(true);
    setError(null);
    setResults(null);

    try {
      if (searchType === 'ingredient') {
        await handleIngredientSearch(query.trim());
      } else if (searchType === 'epaNumber') {
        await handleEpaSearch(query.trim());
      } else {
        await handleTradeNameSearch(query.trim());
      }
    } catch (err) {
      setError(err.message || 'Search failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [searchType, query]);

  async function handleTradeNameSearch(name) {
    // Step 1: Find matching EPA numbers from Firestore index
    const epaMatches = await findEpaByTradeName(name);

    if (!epaMatches.length) {
      setResults({ type: 'tradeName', query: name, products: [], notFound: true });
      return;
    }

    // Step 2: For each unique EPA number, call PICOL live
    const allProducts = [];
    const seen = new Set();

    for (const match of epaMatches.slice(0, 10)) { // limit to 10 EPA numbers
      try {
        const products = await searchByEpa(match.epa);
        for (const p of products) {
          const key = `${p.Id}-${p.Name}`;
          if (!seen.has(key)) {
            seen.add(key);
            allProducts.push(p);
          }
        }
      } catch (err) {
        console.warn(`PICOL lookup failed for EPA ${match.epa}:`, err.message);
      }
    }

    if (!allProducts.length) {
      setResults({ type: 'tradeName', query: name, products: [], notFound: true });
      return;
    }

    const enriched = allProducts.map(p => ({
      ...p,
      hhpSummary: getHHPSummary(p.Ingredients || []),
      stateStatus: getStateStatus([p])
    }));

    setResults({ type: 'tradeName', query: name, products: enriched });
  }

  async function handleEpaSearch(epa) {
    const products = await searchByEpa(epa);

    if (!products.length) {
      setResults({ type: 'epaNumber', query: epa, products: [], notFound: true });
      return;
    }

    const enriched = products.map(p => ({
      ...p,
      hhpSummary: getHHPSummary(p.Ingredients || []),
      stateStatus: getStateStatus([p])
    }));

    setResults({ type: 'epaNumber', query: epa, products: enriched });
  }

  async function handleIngredientSearch(ingredientName) {
    // 1. Check HHP status locally
    const hhpStatus = checkIngredientStatus(ingredientName);

    // 2. Check PICOL for products containing this ingredient
    // For ingredient search, we search PICOL by trade name using the ingredient
    // and filter results — or use GetLabelsByIngredientIds if we have the ID
    let picolProducts = [];
    let picolError = null;

    try {
      // Search EPA index for products matching the ingredient name
      const epaMatches = await findEpaByTradeName(ingredientName);
      for (const match of epaMatches.slice(0, 10)) {
        try {
          const products = await searchByEpa(match.epa);
          for (const p of products) {
            // Only include if this product actually contains the ingredient
            const hasIngredient = (p.Ingredients || []).some(i =>
              i.Name.toUpperCase().includes(ingredientName.toUpperCase()) ||
              ingredientName.toUpperCase().includes(i.Name.toUpperCase().split(',')[0])
            );
            if (hasIngredient) picolProducts.push(p);
          }
        } catch (err) {
          console.warn('PICOL lookup failed:', err.message);
        }
      }
    } catch (err) {
      picolError = 'Could not retrieve product list from PICOL.';
    }

    // Determine WA/OR registration from all returned products
    const stateStatus = getStateStatus(picolProducts);

    setResults({
      type: 'ingredient',
      query: ingredientName,
      hhpStatus,
      stateStatus,
      products: picolProducts.map(p => ({
        ...p,
        hhpSummary: getHHPSummary(p.Ingredients || []),
        stateStatus: getStateStatus([p])
      })),
      picolError
    });
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSearch();
  };

  const currentType = SEARCH_TYPES.find(t => t.id === searchType);

  return (
    <div className="search-tool">
      <div className="search-card">
        <h2 className="search-card-title">Search the HHP Database</h2>
        <p className="search-card-subtitle">
          Search by product trade name, EPA registration number, or active ingredient
        </p>

        {/* Search type tabs */}
        <div className="search-type-tabs">
          {SEARCH_TYPES.map(type => (
            <button
              key={type.id}
              className={`search-tab ${searchType === type.id ? 'active' : ''}`}
              onClick={() => { setSearchType(type.id); setQuery(''); setResults(null); setError(null); }}
            >
              {type.label}
            </button>
          ))}
        </div>

        {/* Search input */}
        <div className="search-input-row">
          <div className="search-input-wrap">
            <span className="search-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
              </svg>
            </span>
            <input
              type="text"
              className="search-input"
              placeholder={currentType.placeholder}
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              autoFocus
            />
            {query && (
              <button className="search-clear" onClick={() => { setQuery(''); setResults(null); setError(null); }}>×</button>
            )}
          </div>
          <button
            className="search-btn"
            onClick={handleSearch}
            disabled={loading || !query.trim()}
          >
            {loading ? <span className="btn-spinner" /> : 'Search'}
          </button>
        </div>

        {/* Search tips */}
        <p className="search-tip">
          <strong>Some searches may take a minute to load — patience grasshopper.</strong>
          {' '}
          {searchType === 'tradeName' && 'Tip: Enter a partial name — results will include all matching products.'}
          {searchType === 'epaNumber' && 'Tip: Enter the full EPA number including the hyphen (e.g. 524-445).'}
          {searchType === 'ingredient' && 'Tip: Use the common chemical name (e.g. "chlorpyrifos" not "Lorsban").'}
        </p>
      </div>

      {/* Error */}
      {error && (
        <div className="result-error">
          <span className="error-icon">⚠</span>
          <p>{error}</p>
        </div>
      )}

      {/* Results */}
      {results && !loading && (
        <div className="results-section">
          {results.type === 'ingredient' ? (
            <IngredientResults results={results} />
          ) : (
            <ProductResults results={results} />
          )}
        </div>
      )}
    </div>
  );
}

// Results for trade name / EPA searches
function ProductResults({ results }) {
  if (results.notFound || results.products.length === 0) {
    return (
      <div className="no-results">
        <div className="no-results-icon">🔍</div>
        <h3>No products found</h3>
        <p>No PICOL records match <strong>"{results.query}"</strong>.</p>
        <p className="no-results-note">
          This product may not be registered for use in Washington or Oregon State.
          Verify with <a href="https://picol.cahnrs.wsu.edu" target="_blank" rel="noreferrer">WSU PICOL</a> directly.
        </p>
      </div>
    );
  }

  // Aggregate HHP status across all returned products
  const allIngredients = [];
  const ingredientsSeen = new Set();
  for (const p of results.products) {
    for (const ing of (p.Ingredients || [])) {
      if (!ingredientsSeen.has(ing.Name)) {
        ingredientsSeen.add(ing.Name);
        allIngredients.push(ing);
      }
    }
  }
  const hhpSummary = getHHPSummary(allIngredients);
  const overallStateStatus = getStateStatus(results.products);

  return (
    <>
      {/* Summary banner */}
      <div className={`summary-banner ${
        hhpSummary.prohibited.length > 0 ? 'banner-prohibited' :
        hhpSummary.watchlist.length > 0 ? 'banner-watchlist' : 'banner-clear'
      }`}>
        <div className="banner-left">
          <div className="banner-status-icon">
            {hhpSummary.prohibited.length > 0 ? '⛔' :
             hhpSummary.watchlist.length > 0 ? '⚠' : '✓'}
          </div>
          <div>
            <div className="banner-status-label">
              {hhpSummary.prohibited.length > 0 ? 'PROHIBITED MATERIAL' :
               hhpSummary.watchlist.length > 0 ? 'HHP WATCH LIST' : 'NOT ON HHP LIST'}
            </div>
            <div className="banner-query">"{results.query}"</div>
          </div>
        </div>
        <div className="banner-state-chips">
          <StateChip state="WA" status={overallStateStatus.washington} />
          <StateChip state="OR" status={overallStateStatus.oregon} />
        </div>
      </div>

      {/* Ingredient breakdown */}
      {(hhpSummary.prohibited.length > 0 || hhpSummary.watchlist.length > 0) && (
        <div className="ingredient-flags">
          <h4 className="section-label">Flagged Active Ingredients</h4>
          <div className="ingredient-list">
            {hhpSummary.prohibited.map(ing => (
              <div key={ing.originalName} className="ingredient-row flag-prohibited">
                <span className="ing-status-dot" />
                <span className="ing-name">{ing.originalName}</span>
                <span className="ing-badge ing-badge-prohibited">PROHIBITED</span>
                <span className="ing-note">International convention — banned substance</span>
              </div>
            ))}
            {hhpSummary.watchlist.map(ing => (
              <div key={ing.originalName} className="ingredient-row flag-watchlist">
                <span className="ing-status-dot" />
                <span className="ing-name">{ing.originalName}</span>
                <span className="ing-badge ing-badge-watchlist">WATCH LIST</span>
                <CriteriaChips criteria={ing.criteria} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Product list */}
      <div className="products-section">
        <h4 className="section-label">
          {results.products.length} Product{results.products.length !== 1 ? 's' : ''} Found in PICOL
        </h4>
        <div className="product-list">
          {results.products.map((product, i) => (
            <ResultCard key={`${product.Id}-${i}`} product={product} />
          ))}
        </div>
      </div>
    </>
  );
}

// Results for active ingredient search
function IngredientResults({ results }) {
  const { hhpStatus, stateStatus, products, query, picolError } = results;

  return (
    <>
      {/* Ingredient HHP status banner */}
      <div className={`summary-banner ${
        hhpStatus.status === 'prohibited' ? 'banner-prohibited' :
        hhpStatus.status === 'watchlist' ? 'banner-watchlist' : 'banner-clear'
      }`}>
        <div className="banner-left">
          <div className="banner-status-icon">
            {hhpStatus.status === 'prohibited' ? '⛔' :
             hhpStatus.status === 'watchlist' ? '⚠' : '✓'}
          </div>
          <div>
            <div className="banner-status-label">
              {hhpStatus.status === 'prohibited' ? 'PROHIBITED MATERIAL' :
               hhpStatus.status === 'watchlist' ? 'HHP WATCH LIST' :
               'NOT ON HHP LIST'}
            </div>
            <div className="banner-query">{query}</div>
          </div>
        </div>
        <div className="banner-state-chips">
          <StateChip state="WA" status={stateStatus.washington} />
          <StateChip state="OR" status={stateStatus.oregon} />
        </div>
      </div>

      {/* WA/OR legal status callout */}
      <div className="state-legal-callout">
        <StateCallout state="Washington" stateCode="WA" status={stateStatus.washington} ingredient={query} />
        <StateCallout state="Oregon" stateCode="OR" status={stateStatus.oregon} ingredient={query} />
      </div>

      {/* Criteria breakdown for watch list */}
      {hhpStatus.status === 'watchlist' && (
        <div className="ingredient-flags">
          <h4 className="section-label">
            Risk Classification for {hhpStatus.name}
            {hhpStatus.riskCategory && (
              <span className="risk-cat-badge"> — {hhpStatus.riskCategory}</span>
            )}
          </h4>
          {hhpStatus.criteria ? (
            <div className="criteria-grid">
              {getCriteriaLabels(hhpStatus.criteria).map(label => (
                <CriteriaItem key={label} label={label} active={true} />
              ))}
              {getCriteriaLabels(hhpStatus.criteria).length === 0 && (
                <p className="no-criteria">See SustainableWA Standard Appendix B for details.</p>
              )}
            </div>
          ) : hhpStatus.riskCategory ? (
            <div className="criteria-grid">
              {hhpStatus.riskCategory.split(',').map(cat => (
                <CriteriaItem key={cat.trim()} label={cat.trim()} active={true} />
              ))}
            </div>
          ) : null}
        </div>
      )}

      {/* Products containing this ingredient */}
      {picolError && (
        <div className="result-error"><span>⚠</span> {picolError}</div>
      )}

      {!picolError && (
        <div className="products-section">
          <h4 className="section-label">
            {products.length > 0
              ? `${products.length} Product${products.length !== 1 ? 's' : ''} Found in PICOL Containing "${query}"`
              : `No PICOL products found matching "${query}"`
            }
          </h4>
          {products.length === 0 && (
            <div className="no-results-inline">
              <p>
                No products labeled for use in Washington or Oregon were found in the PICOL
                database matching this ingredient name.{' '}
                {!stateStatus.washington.registered && !stateStatus.oregon.registered && (
                  <strong>This material does not appear to be registered in WA or OR.</strong>
                )}
              </p>
            </div>
          )}
          {products.length > 0 && (
            <div className="product-list">
              {products.map((product, i) => (
                <ResultCard key={`${product.Id}-${i}`} product={product} />
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
}

// State registration chip
function StateChip({ state, status }) {
  const isLegal = status.registered && status.current;
  const isOld = status.registered && !status.current;

  return (
    <div className={`state-chip ${isLegal ? 'chip-legal' : isOld ? 'chip-old' : 'chip-illegal'}`}>
      <span className="chip-state">{state}</span>
      <span className="chip-status">
        {isLegal ? 'Registered' : isOld ? `Last: ${status.lastYear}` : 'Not Registered'}
      </span>
    </div>
  );
}

// Full state callout for ingredient search
function StateCallout({ state, stateCode, status, ingredient }) {
  const isLegal = status.registered && status.current;
  const isOld = status.registered && !status.current;
  const isNotRegistered = !status.registered;

  if (isLegal) {
    return (
      <div className="state-callout callout-legal">
        <span className="callout-icon">✓</span>
        <div>
          <strong>{ingredient}</strong> has products currently registered in <strong>{state}</strong>.
          <span className="callout-count"> ({status.productCount} product{status.productCount !== 1 ? 's' : ''} found)</span>
        </div>
      </div>
    );
  }

  if (isOld) {
    return (
      <div className="state-callout callout-warning">
        <span className="callout-icon">⚠</span>
        <div>
          Products containing <strong>{ingredient}</strong> were registered in <strong>{state}</strong> as recently
          as {status.lastYear}, but current registration is not confirmed.
          Verify with <a href="https://picol.cahnrs.wsu.edu" target="_blank" rel="noreferrer">PICOL</a>.
        </div>
      </div>
    );
  }

  return (
    <div className="state-callout callout-illegal">
      <span className="callout-icon">⛔</span>
      <div>
        <strong>This material is not legal for use in {state}.</strong>{' '}
        No products containing <strong>{ingredient}</strong> are registered in {state} in the PICOL database.
      </div>
    </div>
  );
}

// Small criteria chips — works with new nested criteria structure
function CriteriaChips({ criteria, riskCategory }) {
  // Use riskCategory string for quick display if full criteria not available
  if (!criteria && riskCategory) {
    return (
      <div className="criteria-chips">
        {riskCategory.split(',').map(c => (
          <span key={c.trim()} className="criteria-chip">{c.trim()}</span>
        ))}
      </div>
    );
  }
  if (!criteria) return null;
  const labels = getCriteriaLabels(criteria);
  if (!labels.length) return null;
  return (
    <div className="criteria-chips">
      {labels.map(c => <span key={c} className="criteria-chip">{c}</span>)}
    </div>
  );
}

// Individual criteria item for ingredient view
function CriteriaItem({ label, active }) {
  return (
    <div className={`criteria-item ${active ? 'criteria-active' : 'criteria-inactive'}`}>
      <span className="criteria-dot" />
      <span>{label}</span>
    </div>
  );
}
